
import React, { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Tabs, TabsContent } from "../ui/tabs";
import { Download, Upload, X, Edit, Check, FileSpreadsheet } from "lucide-react";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "../ui/table";
import apiService from "../../services/apiService";
import { toast } from "../../hooks/use-toast";
import { Input } from "../ui/input";
import {
  parseCSV,
  validateCSV,
  FinancialDataType
} from "../../lib/csvUtils";

interface UploadFinancialsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDataSave: (type: FinancialDataType, data: any) => void;
}

const UploadDebtProfileModal: React.FC<UploadFinancialsModalProps> = ({ 
  isOpen, 
  onClose,
  onDataSave
}) => {
  const [activeTab, setActiveTab] = useState<FinancialDataType>("debt-profile");
  const [csvData, setCsvData] = useState<Record<string, string | number>[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [uploadStep, setUploadStep] = useState<'upload' | 'preview' | 'edit'>('upload');
  const [editingCell, setEditingCell] = useState<{rowIndex: number, column: string} | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = () => {
    setCsvData([]);
    setCsvHeaders([]);
    setUploadStep('upload');
    setEditingCell(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value as FinancialDataType);
    resetState();
  };

  

  const downloadFile = async () => {
    try {
      const response = await apiService.get<Blob>(
        `/customers/download-excel?type=debt`,
        {
          responseType: "blob", // 👈 Important for binary data
        }
      );
  
      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `debt.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const handleDownloadTemplate = () => {
    downloadFile();
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file extension
    if (!file.name.endsWith('.csv')) {
      toast({
        title: "Invalid File Type",
        description: "Please upload a CSV file.",
        variant: "destructive"
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        const parsedCSV = parseCSV(content);
        
        // Validate the CSV
        const validation = validateCSV(activeTab, parsedCSV);
        if (!validation.isValid) {
          toast({
            title: "Invalid CSV Format",
            description: validation.errors.join(". "),
            variant: "destructive"
          });
          return;
        }
        
        setCsvHeaders(parsedCSV.headers);
        setCsvData(parsedCSV.data);
        setUploadStep('preview');
        
        toast({
          title: "CSV Uploaded Successfully",
          description: `${file.name} has been uploaded and is ready for review.`
        });
      } catch (error) {
        console.error("Error parsing CSV:", error);
        toast({
          title: "Failed to Parse CSV",
          description: "The uploaded file could not be parsed. Please check the format.",
          variant: "destructive"
        });
      }
    };
    reader.readAsText(file);
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleSaveData = () => {
    try {
      // Process the data here (e.g., mapping to internal models)
      onDataSave(activeTab, csvData);
      resetState();
      
      toast({
        title: "Data Saved Successfully",
        description: `Financial data has been updated.`
      });
      
      onClose();
    } catch (error) {
      console.error("Error saving data:", error);
      toast({
        title: "Failed to Save Data",
        description: "An error occurred while saving the data. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleEditCell = (rowIndex: number, column: string, value: string | number) => {
    setEditingCell({ rowIndex, column });
    setEditValue(String(value));
  };

  const handleSaveEdit = () => {
    if (!editingCell) return;
    
    const { rowIndex, column } = editingCell;
    const newData = [...csvData];
    
    // Try to convert to number if possible
    const newValue = !isNaN(Number(editValue)) ? Number(editValue) : editValue;
    
    newData[rowIndex] = {
      ...newData[rowIndex],
      [column]: newValue
    };
    
    setCsvData(newData);
    setEditingCell(null);
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
  };

  const renderUploadSection = () => (
    <div className="flex flex-col items-center justify-center py-12 space-y-6">
      <div className="bg-blue-50 p-8 rounded-full">
        <FileSpreadsheet className="h-16 w-16 text-blue-500" />
      </div>
      
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">Upload Debt Profile</h3>
        <p className="text-sm text-gray-500 mb-4">
          Download our template, fill it with your data, and upload it back.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button 
            variant="outline"
            className="flex items-center gap-2"
            onClick={handleDownloadTemplate}
          >
            <Download className="h-4 w-4" />
            Download Template
          </Button>
          
          <Button 
            className="flex items-center gap-2"
            onClick={triggerFileInput}
          >
            <Upload className="h-4 w-4" />
            Upload CSV
          </Button>
          
          <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            className="hidden"
            onChange={handleFileUpload}
          />
        </div>
      </div>
    </div>
  );

  const renderPreviewSection = () => {
    // Find section headers (rows where the first column contains a section name like "ASSETS" with no values)
    const sectionHeaders = csvData
      .map((row, index) => {
        const firstColumn = String(row[csvHeaders[0]]);
        const hasValues = csvHeaders.slice(1).some(header => row[header] !== '');
        return !hasValues && firstColumn.trim() !== '' ? index : null;
      })
      .filter((index): index is number => index !== null);

    return (
      <div className="py-4">
        <div className="flex justify-between mb-4">
          <h3 className="text-lg font-semibold">Preview Financial Data</h3>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setUploadStep('edit')}
              className="flex items-center gap-1"
            >
              <Edit className="h-3.5 w-3.5" />
              Edit Data
            </Button>
            <Button 
              size="sm" 
              onClick={handleSaveData}
              className="flex items-center gap-1"
            >
              <Check className="h-3.5 w-3.5" />
              Save Data
            </Button>
          </div>
        </div>
        
        <div className="border rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {csvHeaders.map((header, index) => (
                    <TableHead key={index} className={index === 0 ? "sticky left-0 bg-white z-10" : ""}>
                      {header}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {csvData.map((row, rowIndex) => (
                  <TableRow 
                    key={rowIndex} 
                    className={sectionHeaders.includes(rowIndex) ? "bg-gray-100 font-semibold" : ""}
                  >
                    {csvHeaders.map((header, colIndex) => (
                      <TableCell 
                        key={`${rowIndex}-${colIndex}`}
                        className={
                          colIndex === 0 
                            ? "sticky left-0 bg-white z-10" + (sectionHeaders.includes(rowIndex) ? " bg-gray-100" : "")
                            : ""
                        }
                      >
                        {row[header] !== undefined ? String(row[header]) : ''}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    );
  };

  const renderEditSection = () => {
    const sectionHeaders = csvData
      .map((row, index) => {
        const firstColumn = String(row[csvHeaders[0]]);
        const hasValues = csvHeaders.slice(1).some(header => row[header] !== '');
        return !hasValues && firstColumn.trim() !== '' ? index : null;
      })
      .filter((index): index is number => index !== null);
    
    return (
      <div className="py-4">
        <div className="flex justify-between mb-4">
          <h3 className="text-lg font-semibold">Edit Financial Data</h3>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setUploadStep('preview')}
              className="flex items-center gap-1"
            >
              Cancel
            </Button>
            <Button 
              size="sm" 
              onClick={handleSaveData}
              className="flex items-center gap-1"
            >
              <Check className="h-3.5 w-3.5" />
              Save Data
            </Button>
          </div>
        </div>
        
        <div className="border rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {csvHeaders.map((header, index) => (
                    <TableHead key={index} className={index === 0 ? "sticky left-0 bg-white z-10" : ""}>
                      {header}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {csvData.map((row, rowIndex) => (
                  <TableRow 
                    key={rowIndex} 
                    className={sectionHeaders.includes(rowIndex) ? "bg-gray-100 font-semibold" : ""}
                  >
                    {csvHeaders.map((header, colIndex) => (
                      <TableCell 
                        key={`${rowIndex}-${colIndex}`}
                        className={
                          colIndex === 0 
                            ? "sticky left-0 bg-white z-10" + (sectionHeaders.includes(rowIndex) ? " bg-gray-100" : "")
                            : ""
                        }
                      >
                        {editingCell?.rowIndex === rowIndex && editingCell?.column === header ? (
                          <div className="flex items-center gap-1">
                            <Input
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="h-8 w-full"
                              autoFocus
                            />
                            <div className="flex gap-1">
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                className="h-7 w-7" 
                                onClick={handleSaveEdit}
                              >
                                <Check className="h-3.5 w-3.5" />
                              </Button>
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                className="h-7 w-7" 
                                onClick={handleCancelEdit}
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div 
                            className={`px-1 py-0.5 rounded hover:bg-gray-100 cursor-pointer ${sectionHeaders.includes(rowIndex) ? "pointer-events-none" : ""}`}
                            onClick={() => !sectionHeaders.includes(rowIndex) && handleEditCell(rowIndex, header, row[header] || '')}
                          >
                            {row[header] !== undefined ? String(row[header]) : ''}
                          </div>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Upload Debt Profile</DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="debt-profile" onValueChange={handleTabChange} className="flex-1 overflow-hidden flex flex-col">
          {/* <TabsList className="mb-4 grid w-full grid-cols-3">
            <TabsTrigger value="balance-sheet">Balance Sheet</TabsTrigger>
            <TabsTrigger value="profit-loss">Profit & Loss</TabsTrigger>
            <TabsTrigger value="cash-flow">Cash Flow</TabsTrigger>
          </TabsList> */}
          
          <div className="flex-1 overflow-y-auto pr-1">
            <TabsContent value="debt-profile" className="mt-0">
              {uploadStep === 'upload' && renderUploadSection()}
              {uploadStep === 'preview' && renderPreviewSection()}
              {uploadStep === 'edit' && renderEditSection()}
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default UploadDebtProfileModal;
